import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Card, CardContent, Skeleton, Typography } from '@mui/material'

import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatDropOverlay } from '@/components/chat/ChatDropOverlay'
import { ChatThread } from '@/components/chat/ChatThread'
import { useChatComposer } from '@/components/chat/useChatComposer'
import { useChatPresence } from '@/components/chat/useChatPresence'
import type { ChatMessage } from '@/api/chat'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { getErrorMessage } from '@/utils/error'
import type { LessonStatus } from '@/types'

interface LessonChatPanelProps {
  lessonId: number
  /** Статус урока — от него зависит право писать, поэтому смена перечитывает комнату. */
  lessonStatus: LessonStatus
}

/**
 * Чат урока на странице урока: та же лента и тот же композер, что на странице
 * чата.
 *
 * Писать можно только пока урок идёт — это решает сервер (`can_write`), панель
 * лишь объясняет пользователю, почему поле недоступно.
 */
export function LessonChatPanel({ lessonId, lessonStatus }: LessonChatPanelProps) {
  const { t } = useTranslation()
  const currentUserId = useAuthStore((s) => s.user?.id ?? 0)

  const room = useChatStore((s) => s.lessonRooms[lessonId] ?? null)
  const fetchLessonRoom = useChatStore((s) => s.fetchLessonRoom)
  const clearLessonRoom = useChatStore((s) => s.clearLessonRoom)
  const deleteMessage = useChatStore((s) => s.deleteMessage)
  const toggleReaction = useChatStore((s) => s.toggleReaction)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const roomId = room?.id ?? null
  const canWrite = room?.can_write ?? false
  // Имя автора нужно только в групповом уроке: в паре учитель-ученик оно
  // очевидно и в узкой колонке отнимает по строке на каждое сообщение.
  const showAuthor = (room?.members?.length ?? 0) > 2

  /**
   * Поколение запроса комнаты. Смена статуса урока перезапускает загрузку, и
   * ответы могут прийти не в том порядке, в каком ушли: применяем только
   * ответ последнего запроса, иначе опоздавший затирал бы свежий.
   */
  const requestGenerationRef = useRef(0)

  // Комната перечитывается на смене статуса урока: старт и завершение меняют
  // can_write, а событие о них приходит по каналу урока, не по каналу чата.
  // Комната при этом из стора не убирается — иначе на каждой смене статуса
  // рвалась бы подписка на её канал, а сообщения этого окна не догрузить.
  useEffect(() => {
    const generation = ++requestGenerationRef.current
    setIsLoading(true)

    void fetchLessonRoom(lessonId).then(({ error: loadError }) => {
      if (requestGenerationRef.current !== generation) return

      setIsLoading(false)
      setError(loadError)
    })
  }, [lessonId, lessonStatus, fetchLessonRoom])

  // Уходя со страницы, отпускаем комнату: иначе за сессию накапливаются
  // десятки комнат уроков, и каждая держит свой realtime-канал. Поколение
  // сдвигается здесь же — ответ в полёте после ухода ничего не вернёт назад.
  useEffect(
    () => () => {
      requestGenerationRef.current += 1
      clearLessonRoom(lessonId)
    },
    [lessonId, clearLessonRoom]
  )

  useChatPresence(roomId)

  const composer = useChatComposer(roomId, canWrite, setError)

  const handleDelete = useCallback(
    (message: ChatMessage) => {
      if (roomId === null) return
      deleteMessage(roomId, message.id).catch((e: unknown) => setError(getErrorMessage(e)))
    },
    [roomId, deleteMessage]
  )

  const handleToggleReaction = useCallback(
    (messageId: number, emoji: string) => {
      if (roomId === null) return
      void toggleReaction(roomId, messageId, emoji, currentUserId)
    },
    [roomId, toggleReaction, currentUserId]
  )

  const handleCopyText = useCallback(
    (message: ChatMessage) => {
      const value = message.text ?? ''
      if (!value.trim()) return
      if (typeof navigator.clipboard?.writeText !== 'function') {
        setError(t('chat.copyFailed'))
        return
      }
      navigator.clipboard.writeText(value).catch(() => setError(t('chat.copyFailed')))
    },
    [t]
  )

  // Причина берётся из статуса урока, но писать может быть нельзя и по другой
  // причине: администратор видит урок, участником не будучи. Для него — общий
  // текст, а не «урок завершён» на идущем уроке.
  const readOnlyHint = (() => {
    if (lessonStatus === 'planned') return t('chat.lessonChatBeforeStart')
    if (lessonStatus === 'canceled') return t('chat.lessonChatCanceled')
    if (lessonStatus === 'completed') return t('chat.lessonChatAfterFinish')

    return t('chat.lessonChatNoAccess')
  })()

  return (
    <Card sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Отступы карточки урезаны: внутри ещё рамка чата со своими, и на
          колонке в 360px двойной отступ съедал шестую часть ширины */}
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          gap: 1,
          p: 1.5,
          '&:last-child': { pb: 1.5 },
        }}
      >
        <Typography variant="h6" component="div" fontWeight={700}>
          {t('chat.lessonChatTitle')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading && roomId === null ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} width="70%" />
            <Skeleton variant="rounded" height={40} />
          </Box>
        ) : roomId === null ? (
          <Typography variant="body2" color="text.secondary">
            {lessonStatus === 'planned'
              ? t('chat.lessonChatBeforeStart')
              : t('chat.lessonChatUnavailable')}
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 320,
              maxHeight: 480,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
            {...composer.dragProps}
          >
            {composer.isDragOver && canWrite && <ChatDropOverlay />}
            <ChatThread
              roomId={roomId}
              showAuthor={showAuthor}
              canWrite={canWrite}
              currentUserId={currentUserId}
              onEdit={composer.startEdit}
              onDelete={handleDelete}
              onCopyText={handleCopyText}
              onToggleReaction={handleToggleReaction}
              emptyText={t('chat.lessonChatEmpty')}
              dense
            />
            <ChatComposer composer={composer} readOnlyHint={readOnlyHint} dense />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
